import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Bot,
  FileSpreadsheet,
  Mail
} from "lucide-react";
import { LanguageStore } from "@/Services/LanguageStore";
import { BridgeLanguage } from "@/Services/Language";
import { decodeWithBase64 } from "@/Services/encodedecode";
import { useToast } from "@/hooks/use-toast";

interface DeploymentDialogProps {
  onClose: () => void;
  onDeploy: (deployment: { name: string; sheetsUrl: string; groupEmail: string; subject: string; body: string; emailLimit: number; domains: string[] }) => void;
}

const DeploymentDialog = ({ onClose, onDeploy }: DeploymentDialogProps) => {
  const { toast } = useToast();
  const defaultName = "Cosmitude Email tracker agent";
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [groupEmail, setGroupEmail] = useState("");
  const [isDeploying, setIsDeploying] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [deploymentName, setDeploymentName] = useState(defaultName);
  const [dailyLimit, setDailyLimit] = useState(450);
  const [minEmailLimit, setMinEmailLimit] = useState(1);
  const [maxEmailLimit, setMaxEmailLimit] = useState(2000);
  const [blockedDomain, setBlockedDomain] = useState("");
  const [blockedDomainList, setBlockedDomainList] = useState<string[]>([]);

  const SHEETS_RE = /^https?:\/\/docs\.google\.com\/spreadsheets\/d\/[A-Za-z0-9-_]+/;
  const isValidSheets = useMemo(() => SHEETS_RE.test(sheetsUrl), [sheetsUrl]);
  const canSubmit = isValidSheets && subject.trim().length > 0 && body.trim().length > 0;

  useEffect(() => {
    checkForEmailType();
  }, [])

  const checkForEmailType = () => {
    try {
      let userProfile = JSON.parse(localStorage.getItem("userProfile"));
      if (!userProfile || !userProfile.emailId) {
        setMaxEmailLimit(500);
        return;
      }

      const emailId = userProfile.emailId.toLowerCase();
      const domain = emailId.split("@")[1];

      // List of common public email providers
      const publicDomains = [
        "gmail.com",
        "yahoo.com",
        "outlook.com",
        "hotmail.com",
        "icloud.com",
        "proton.me",
        "protonmail.com",
        "live.com"
      ];

      const isPublicEmail = publicDomains.includes(domain);
      if (isPublicEmail) {
        setMaxEmailLimit(500);
      }
      else {
        setMaxEmailLimit(2000);
      }

    } catch (error) {
      console.log(error)
    }
  }

  const handleDeploy = () => {
    const validSheet = sheetsUrl && sheetsUrl.includes('docs.google.com/spreadsheets');
    // const validGroup = /@/.test(groupEmail);
    // if (!validSheet || !validGroup) return;
    if (!validSheet) return;
    if (!deploymentName) return;
    setIsDeploying(true);
    onDeploy({ name: deploymentName, sheetsUrl, groupEmail, subject, body, emailLimit: dailyLimit, domains: blockedDomainList });
  };

  const LanguageString = (number: any) => {
    let LangData = LanguageStore.allLanguages;
    if (Object.keys(LangData).length === 0) {
      let localLang = localStorage.getItem('BridgeLanguageStrings');
      if (localLang) {
        LangData = JSON.parse(decodeWithBase64(localLang));
      }
    }
    if (LangData && LangData[number]) {
      return LangData[number];
    }
    else {
      return BridgeLanguage(number);
    }
  }

  const isValidDomain = (domain: string) => {
    const cleaned = domain.trim().toLowerCase().replace(/^@/, "");

    const domainRegex =
      /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.[a-z]{2,})+$/;

    return domainRegex.test(cleaned);
  };

  const checkForValidDomain = (domain: string) => {
    if (!domain) return;

    const normalizedDomain = domain
      .trim()
      .toLowerCase()
      .replace(/^@/, "");

    if (!isValidDomain(normalizedDomain)) {
      toast({ variant: "destructive", title: LanguageString("2204") });
      return;
    }

    if (blockedDomainList.includes(normalizedDomain)) {
      toast({ title: LanguageString("2203") }); // already exists
      return;
    }
    setBlockedDomainList(prev => [...prev, normalizedDomain]);
    setBlockedDomain("");
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Mail className="h-6 w-6 text-blue-600" />
            <span>{LanguageString("2013")}</span>
          </DialogTitle>
          <DialogDescription>
            {LanguageString("2058")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-1">
            <Label>{LanguageString("2059")}</Label>
            <Input id="deploymentName" value={deploymentName}
              onChange={(e) => setDeploymentName(e.target.value)}
              placeholder={LanguageString("2059")}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="sheetsUrl">{LanguageString("2060")}</Label>
            <div className="flex gap-2">
              <Input
                id="sheetsUrl"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={sheetsUrl}
                onChange={(e) => setSheetsUrl(e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-500">{LanguageString("2061")}</p>
          </div>

          {/* <div className="space-y-1">
            <Label htmlFor="groupEmail">Google Group email</Label>
            <div className="flex gap-2">
              <Input
                id="groupEmail"
                placeholder="team@googlegroups.com"
                value={groupEmail}
                onChange={(e) => setGroupEmail(e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-500">This address receives and aggregates replies.</p>
          </div> */}

          <div className="space-y-1">
            <Label htmlFor="subject">{LanguageString("2062")}</Label>
            <Input
              id="subject"
              placeholder={LanguageString("2063")}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && canSubmit && handleDeploy()}
            />
            <p className="text-xs text-gray-500">
              {LanguageString("2064")} “{subject}”.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="body">{LanguageString("2047")}</Label>
            <textarea
              id="body"
              className="w-full rounded-md border p-2 min-h-[160px]"
              placeholder={LanguageString("2065")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            {/* Label + Value Row */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-700">
                {LanguageString("2211")}
              </Label>
              <span className="text-sm font-semibold text-blue-600">
                {dailyLimit}
              </span>
            </div>

            {/* Slider */}
            <input
              type="range"
              min={minEmailLimit}
              max={maxEmailLimit}
              step={1}
              value={dailyLimit}
              onChange={(e) => setDailyLimit(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #2563eb 0%, #2563eb ${(dailyLimit / maxEmailLimit) * 100
                  }%, #e5e7eb ${(dailyLimit / maxEmailLimit) * 100}%, #e5e7eb 100%)`
              }}
            />

            {/* Optional Min/Max */}
            <div className="flex justify-between text-xs text-gray-400">
              <span>{minEmailLimit}</span>
              <span>{maxEmailLimit}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">{LanguageString("2197")}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="blockDomain"
                className="flex-1"
                value={blockedDomain}
                onChange={(e) =>
                  setBlockedDomain(e.target.value.trim().toLowerCase())
                }
                onKeyDown={(e) =>
                  e.key === "Enter" && canSubmit && handleDeploy()
                }
                placeholder="eg., rediff.com"
              />

              <Button
                size="sm"
                variant="outline"
                onClick={() => checkForValidDomain(blockedDomain)}
              >
                {LanguageString("2202")}
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {blockedDomainList.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {LanguageString("2197")}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {blockedDomainList.map((domain, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1 rounded-full text-sm"
                      >
                        <span>{domain}</span>
                        <button
                          onClick={() =>
                            setBlockedDomainList(blockedDomainList.filter((_, i) => i !== index))
                          }
                          className="hover:text-red-900"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        <DialogFooter className="flex justify-between">
          <div />
          <div className="space-x-2">
            <Button variant="outline" onClick={onClose}>{LanguageString("Cancel")}</Button>
            <Button
              onClick={handleDeploy}
              disabled={
                isDeploying ||
                !sheetsUrl.includes('docs.google.com/spreadsheets') ||
                !subject.trim().length ||
                !body.trim().length ||
                !deploymentName
              }
            >
              {isDeploying ? LanguageString("2066") : LanguageString("2013")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeploymentDialog;